/**
 * Etapa 4 da Fábrica de Vídeos — GERAÇÃO no BI pela API paga do Higgsfield (serviço 8105).
 *
 * Regra da Mariana (24/09/2026): ver a quantidade de créditos e o preço ANTES de seguir. Por isso
 * a ordem aqui é fixa: salvar o briefing → estimativa (grátis) → aprovar roteiro → gerar cena
 * (custa) → aprovar cena → gerar 4 módulos (custa) → aprovar cada um → montar (0). Cada botão que
 * gasta mostra o valor e pede confirmação; o serviço recusa sem {confirmo: 'GERAR'}.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Divider, LinearProgress, Link, Stack, TextField, Typography } from '@mui/material'
import {
  MODULOS,
  ROTULO_ETAPA,
  briefingPronto,
  checarProduto,
  emGeracao,
  fmtBrlBr,
  fmtCred,
  fmtUsdBr,
  linhaCusto,
  roteiroOk,
  type Briefing,
  type EtapaGeracao as EtapaId,
  type ModuloId,
} from './logic'
import {
  FabricaVideosApiError,
  criarProjeto,
  editarProjeto,
  estimar,
  fetchProjeto,
  gerarCena,
  gerarModulos,
  montar,
  salvarBriefing,
  urlArquivo,
  type Projeto,
  type StatusServico,
} from './fabricaVideosService'

const etapaDe = (p: Projeto): EtapaId => {
  if (p.montagem.status === 'ok') return 'pronto'
  if (MODULOS.every((m) => p.modulos[m.id].aprovacao === 'aprovado')) return 'montagem'
  if (p.cena.aprovacao === 'aprovado') return 'modulos'
  if (p.roteiro_aprovado) return 'cena'
  return 'roteiro'
}

function Confirmar({
  rotulo,
  custo,
  onConfirmar,
  disabled,
  cor = 'primary',
}: {
  rotulo: string
  custo: string
  onConfirmar: () => Promise<void>
  disabled?: boolean
  cor?: 'primary' | 'warning'
}) {
  const [aberto, setAberto] = useState(false)
  const [rodando, setRodando] = useState(false)
  if (!aberto) {
    return (
      <Button variant="contained" color={cor} disabled={disabled} onClick={() => setAberto(true)}>
        {rotulo} · {custo}
      </Button>
    )
  }
  return (
    <Alert
      severity="warning"
      action={
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setAberto(false)} disabled={rodando}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={rodando}
            onClick={async () => {
              setRodando(true)
              try {
                await onConfirmar()
                setAberto(false)
              } finally {
                setRodando(false)
              }
            }}
          >
            {rodando ? <CircularProgress size={16} /> : 'Confirmar e gastar'}
          </Button>
        </Stack>
      }
    >
      <b>{rotulo}</b> vai gastar <b>{custo}</b> na API do Higgsfield. Confirma?
    </Alert>
  )
}

function Video({ src }: { src: string }) {
  return (
    <Box
      component="video"
      src={src}
      controls
      playsInline
      sx={{ width: '100%', maxWidth: 270, aspectRatio: '9 / 16', borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'black' }}
    />
  )
}

export function EtapaGeracao({
  b,
  set,
  status,
}: {
  b: Briefing
  set: (f: (x: Briefing) => Briefing) => void
  status: StatusServico | null
}) {
  const [p, setP] = useState<Projeto | null>(null)
  const [pendRoteiro, setPendRoteiro] = useState<string[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [ajusteCena, setAjusteCena] = useState('')
  const timer = useRef<number | null>(null)

  const falhou = (e: unknown) => setErro(e instanceof Error ? e.message : String(e))

  const carregar = useCallback(async (id: string) => {
    const r = await fetchProjeto(id)
    setP(r.projeto)
    setPendRoteiro(r.pendencias_roteiro)
    return r.projeto
  }, [])

  // 1. Salvar o briefing no serviço (cria ou atualiza) e pedir a estimativa (grátis).
  const salvarEEstimar = useCallback(async () => {
    setErro(null)
    setOcupado('salvando')
    try {
      let id = b.projetoId
      let proj: Projeto
      if (id) {
        try {
          proj = (await salvarBriefing(id, b)).projeto
        } catch (e) {
          if (e instanceof FabricaVideosApiError && e.status === 404) {
            id = ''
            proj = (await criarProjeto(b)).projeto
          } else throw e
        }
      } else {
        proj = (await criarProjeto(b)).projeto
      }
      if (proj.id !== b.projetoId) set((x) => ({ ...x, projetoId: proj.id }))
      setP(proj)
      if (status?.higgsfield.chave) {
        setOcupado('estimando')
        const est = await estimar(proj.id)
        setP((q) => (q ? { ...q, estimativa: est.estimativa } : q))
      }
      await carregar(proj.id)
    } catch (e) {
      falhou(e)
    } finally {
      setOcupado(null)
    }
  }, [b, set, status, carregar])

  // Ao entrar na etapa: carrega o projeto salvo (se houver). Não estima sozinho — botão explícito.
  useEffect(() => {
    if (b.projetoId) carregar(b.projetoId).catch((e) => (e instanceof FabricaVideosApiError && e.status === 404 ? set((x) => ({ ...x, projetoId: '' })) : falhou(e)))
  }, [b.projetoId, carregar, set])

  // Enquanto algo está sendo gerado, consulta o serviço a cada 4 s.
  useEffect(() => {
    if (!p || !emGeracao(p)) {
      if (timer.current) window.clearInterval(timer.current)
      timer.current = null
      return
    }
    if (timer.current) return
    timer.current = window.setInterval(() => carregar(p.id).catch(() => undefined), 4000)
    return () => {
      if (timer.current) window.clearInterval(timer.current)
      timer.current = null
    }
  }, [p, carregar])

  const acao = async (nome: string, fn: () => Promise<unknown>) => {
    setErro(null)
    setOcupado(nome)
    try {
      await fn()
      if (p) await carregar(p.id)
    } catch (e) {
      falhou(e)
    } finally {
      setOcupado(null)
    }
  }

  const pronto = briefingPronto(b)
  const pendBriefing = [
    ...checarProduto(b.produto).filter((c) => c.nivel === 'erro').map((c) => `Produto: ${c.texto}`),
    ...(roteiroOk(b.roteiro) ? [] : ['Roteiro: tem fala vazia, acima de 12 palavras ou com aspas.']),
  ]
  const semChave = status ? !status.higgsfield.chave : false
  const semFfmpeg = status ? !(status.ffmpeg.ffmpeg && status.ffmpeg.ffprobe) : false
  const est = p?.estimativa ?? null
  const etapa = p ? etapaDe(p) : 'roteiro'
  const briefingMudou = !!p && (JSON.stringify(p.roteiro) !== JSON.stringify(b.roteiro) || p.produto.mlb !== b.produto.mlb || JSON.stringify({ ...p.cena, status: 0, aprovacao: 0, versoes: 0 }).length !== JSON.stringify({ ...p.cena, ...b.cena, status: 0, aprovacao: 0, versoes: 0 }).length)

  return (
    <Stack spacing={2.5}>
      {semChave ? (
        <Alert severity="error">
          O serviço está sem a chave da API do Higgsfield. Sem ela, dá para salvar o briefing e aprovar o roteiro, mas não para
          estimar nem gerar. Ver <code>DEPLOY_VPS.md</code>, passo 3.
        </Alert>
      ) : null}
      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {/* -------- 1. salvar + estimar */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          1. Salvar o briefing e ver o preço (grátis)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A estimativa vem da própria API do Higgsfield para esta configuração (cena 9:16 em 2K + 4 clipes Seedance 2.0 de 5 s em
          1080p com áudio) e é convertida pela PTAX do dia. Nada é gerado neste passo.
        </Typography>
        {pendBriefing.length ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {pendBriefing.map((x) => (
              <Typography key={x} variant="body2">
                • {x}
              </Typography>
            ))}
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant={p ? 'outlined' : 'contained'} onClick={salvarEEstimar} disabled={!pronto || ocupado != null}>
            {ocupado === 'salvando' || ocupado === 'estimando' ? <CircularProgress size={18} /> : p ? 'Salvar de novo e reestimar' : 'Salvar e estimar'}
          </Button>
          {p ? <Chip size="small" label={`Projeto ${p.id} · ${ROTULO_ETAPA[etapa]}`} /> : null}
          {briefingMudou ? <Chip size="small" color="warning" label="briefing mudou desde o último salvar" /> : null}
        </Stack>
        {est ? (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 3 }}>
              <Typography variant="body2">
                Cena base: <b>{linhaCusto({ ...est.itens.cena, brl: est.refazer_cena.brl })}</b>
              </Typography>
              <Typography variant="body2">
                Cada módulo: <b>{linhaCusto(est.refazer_modulo)}</b>
              </Typography>
              <Typography variant="body2">
                Vídeo inteiro (cena + 4 módulos): <b>{fmtCred(est.total.credits)} · {fmtUsdBr(est.total.usd)} · {fmtBrlBr(est.total.brl)}</b>
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Dólar {est.dolar.valor.toLocaleString('pt-BR', { minimumFractionDigits: 4 })} ({est.dolar.origem}
              {est.dolar.data ? `, ${est.dolar.data}` : ''}) + IOF {est.dolar.iof_pct}% · sem IOF: {fmtBrlBr(est.total.brl_sem_iof)} · montagem
              não custa · falha não é cobrada. Estimado em {new Date(est.em).toLocaleString('pt-BR')}.
            </Typography>
          </Box>
        ) : null}
        {p ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Gasto real neste projeto até agora: {fmtCred(p.custo_real.credits)} · {fmtUsdBr(p.custo_real.usd)} · {fmtBrlBr(p.custo_real.brl)} (
            {p.custo_real.geracoes} gerações)
          </Typography>
        ) : null}
      </Box>

      {p ? (
        <>
          <Divider />
          {/* -------- 2. roteiro */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              2. Aprovar o roteiro {p.roteiro_aprovado ? '✓' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              O texto aprovado vai literalmente para o prompt dos 4 clipes e para a legenda. Mudou uma fala? Salve de novo (passo 1) e
              aprove outra vez.
            </Typography>
            {pendRoteiro.length ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {pendRoteiro.join(' · ')}
              </Alert>
            ) : null}
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {MODULOS.map((m) => (
                <Typography key={m.id} variant="body2">
                  <b>{m.n}.</b> {p.roteiro[m.id] || <i>vazio</i>}
                </Typography>
              ))}
            </Stack>
            {!p.roteiro_aprovado ? (
              <Button variant="contained" disabled={ocupado != null || pendRoteiro.length > 0 || briefingMudou} onClick={() => acao('roteiro', () => editarProjeto(p.id, { roteiro_aprovado: true }))}>
                Aprovar roteiro
              </Button>
            ) : (
              <Button size="small" disabled={ocupado != null} onClick={() => acao('roteiro', () => editarProjeto(p.id, { roteiro_aprovado: false }))}>
                Reabrir roteiro
              </Button>
            )}
          </Box>

          <Divider />
          {/* -------- 3. cena */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              3. Cena base {p.cena.aprovacao === 'aprovado' ? '✓' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uma imagem 9:16 da apresentadora segurando o produto, feita a partir da foto principal do anúncio. Ela vira o primeiro
              quadro dos 4 clipes: mesma pessoa, mesma roupa, mesma casa. Confira formato, cor e quantidade do produto antes de aprovar.
            </Typography>
            {p.cena.status === 'gerando' || p.cena.status === 'fila' ? (
              <Box sx={{ mb: 1 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">
                  Gerando a cena… ({p.cena.status_api || 'na fila'}) — costuma levar até 1 minuto.
                </Typography>
              </Box>
            ) : null}
            {p.cena.status === 'erro' ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                A cena falhou (não é cobrada): {p.cena.erro}
              </Alert>
            ) : null}
            {p.cena.status === 'ok' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
                <Box
                  component="img"
                  src={urlArquivo(p.id, 'cena.jpg', `${p.cena.versoes}-${p.cena.em || ''}`)}
                  alt="Cena base"
                  sx={{ width: '100%', maxWidth: 270, aspectRatio: '9 / 16', objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
                />
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Versão {p.cena.versoes} · {p.cena.estimativa ? linhaCusto(p.cena.estimativa) : ''}
                    {p.cena.url ? (
                      <>
                        {' · '}
                        <Link href={p.cena.url} target="_blank" rel="noopener">
                          abrir original
                        </Link>
                      </>
                    ) : null}
                  </Typography>
                  {p.cena.aprovacao !== 'aprovado' ? (
                    <Button variant="contained" color="success" disabled={ocupado != null} onClick={() => acao('cena', () => editarProjeto(p.id, { aprovacao_cena: 'aprovado' }))}>
                      Aprovar cena
                    </Button>
                  ) : (
                    <Chip color="success" label="Cena aprovada" sx={{ alignSelf: 'flex-start' }} />
                  )}
                  <TextField
                    size="small"
                    label="Ajuste para refazer (opcional)"
                    placeholder="cabelo preso, produto mais perto da câmera, sem objetos vermelhos"
                    value={ajusteCena}
                    onChange={(e) => setAjusteCena(e.target.value)}
                  />
                  {est ? (
                    <Confirmar
                      rotulo="Refazer a cena"
                      custo={linhaCusto(est.refazer_cena)}
                      disabled={ocupado != null || semChave || emGeracao(p)}
                      onConfirmar={() => acao('cena', () => gerarCena(p.id, ajusteCena))}
                    />
                  ) : null}
                </Stack>
              </Stack>
            ) : null}
            {p.cena.status !== 'ok' && p.cena.status !== 'gerando' && p.cena.status !== 'fila' ? (
              est ? (
                <Confirmar
                  rotulo="Gerar a cena"
                  custo={linhaCusto({ ...est.itens.cena, brl: est.refazer_cena.brl })}
                  disabled={!p.roteiro_aprovado || ocupado != null || semChave}
                  onConfirmar={() => acao('cena', () => gerarCena(p.id, b.cena.ajuste))}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Faça a estimativa no passo 1 para liberar a geração.
                </Typography>
              )
            ) : null}
          </Box>

          <Divider />
          {/* -------- 4. módulos */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              4. Os 4 módulos ({MODULOS.filter((m) => p.modulos[m.id].aprovacao === 'aprovado').length} de 4 aprovados)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Seedance 2.0, 5 s cada, 1080p, 9:16, com a fala em português e lip-sync. Os 4 rodam em paralelo (2 a 4 minutos). Refazer
              um módulo custa só o preço daquele módulo. Confira: produto igual ao anúncio, fala completa, sem texto desenhado no vídeo.
            </Typography>
            {est && p.cena.aprovacao === 'aprovado' && MODULOS.every((m) => p.modulos[m.id].status !== 'ok' && p.modulos[m.id].status !== 'gerando' && p.modulos[m.id].status !== 'fila') ? (
              <Box sx={{ mb: 1.5 }}>
                <Confirmar
                  rotulo="Gerar os 4 módulos"
                  custo={`${fmtCred(4 * est.refazer_modulo.credits)} · ${fmtUsdBr(4 * est.refazer_modulo.usd)} · ${fmtBrlBr(4 * est.refazer_modulo.brl)}`}
                  disabled={ocupado != null || semChave || emGeracao(p)}
                  onConfirmar={() => acao('modulos', () => gerarModulos(p.id))}
                />
              </Box>
            ) : null}
            {p.cena.aprovacao !== 'aprovado' ? (
              <Typography variant="body2" color="text.secondary">
                Aprove a cena para liberar os módulos.
              </Typography>
            ) : null}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {MODULOS.map((m) => {
                const md = p.modulos[m.id]
                return (
                  <Box key={m.id} sx={{ width: { xs: '100%', sm: 270 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.n}. {m.nome} {md.aprovacao === 'aprovado' ? '✓' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      “{p.roteiro[m.id]}”
                    </Typography>
                    {md.status === 'gerando' || md.status === 'fila' ? (
                      <Box>
                        <LinearProgress />
                        <Typography variant="caption" color="text.secondary">
                          Gerando… ({md.status_api || 'na fila'})
                        </Typography>
                      </Box>
                    ) : null}
                    {md.status === 'erro' ? (
                      <Alert severity="error" sx={{ mb: 0.5 }}>
                        Falhou (não cobrado): {md.erro}
                      </Alert>
                    ) : null}
                    {md.status === 'ok' ? (
                      <>
                        <Video src={urlArquivo(p.id, `${m.id}.mp4`, `${md.versoes}-${md.em || ''}`)} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          v{md.versoes}
                          {md.sonda ? ` · ${md.sonda.duracao}s · ${md.sonda.largura}×${md.sonda.altura}${md.sonda.audio ? ' · áudio' : ' · SEM ÁUDIO'}` : ''}
                          {md.estimativa ? ` · ${linhaCusto(md.estimativa)}` : ''}
                        </Typography>
                        {md.aprovacao !== 'aprovado' ? (
                          <Button size="small" variant="contained" color="success" disabled={ocupado != null} onClick={() => acao('mod', () => editarProjeto(p.id, { aprovacao_modulos: { [m.id]: 'aprovado' } }))}>
                            Aprovar
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {est && (md.status === 'ok' || md.status === 'erro') && !emGeracao(p) ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Confirmar
                          rotulo={md.status === 'erro' ? 'Tentar de novo' : 'Refazer'}
                          custo={linhaCusto(est.refazer_modulo)}
                          cor="warning"
                          disabled={ocupado != null || semChave}
                          onConfirmar={() => acao('mod', () => gerarModulos(p.id, [m.id as ModuloId]))}
                        />
                      </Box>
                    ) : null}
                  </Box>
                )
              })}
            </Stack>
            {MODULOS.every((m) => p.modulos[m.id].status === 'ok') && MODULOS.some((m) => p.modulos[m.id].aprovacao !== 'aprovado') ? (
              <Button size="small" sx={{ mt: 1 }} disabled={ocupado != null} onClick={() => acao('mod', () => editarProjeto(p.id, { aprovacao_modulos: Object.fromEntries(MODULOS.map((m) => [m.id, 'aprovado'])) }))}>
                Aprovar todos
              </Button>
            ) : null}
          </Box>

          <Divider />
          {/* -------- 5. montagem */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              5. Montagem final {p.montagem.status === 'ok' ? '✓' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Junta os 4 clipes em 1080×1920 e queima a legenda karaokê com o roteiro aprovado. Roda na VPS com ffmpeg, sem custo. O
              MP4 serve para Clips do Mercado Livre, Shopee Vídeo, Reels e TikTok.
            </Typography>
            {semFfmpeg ? <Alert severity="warning" sx={{ mb: 1 }}>ffmpeg não está instalado na VPS: a montagem vai falhar. Ver DEPLOY_VPS.md.</Alert> : null}
            {p.montagem.status === 'montando' ? (
              <Box sx={{ mb: 1 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">
                  Montando… {p.montagem.passo || ''}
                </Typography>
              </Box>
            ) : null}
            {p.montagem.status === 'erro' ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {p.montagem.erro}
              </Alert>
            ) : null}
            {p.montagem.status === 'ok' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                <Video src={urlArquivo(p.id, 'final.mp4', p.montagem.em || '')} />
                <Stack spacing={1}>
                  <Typography variant="body2">
                    {p.montagem.duracao?.toFixed(1)} s · {((p.montagem.tamanho || 0) / 1024 / 1024).toFixed(1)} MB
                  </Typography>
                  <Button variant="contained" component="a" href={urlArquivo(p.id, 'final.mp4')} download>
                    Baixar MP4
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Custo real do vídeo: <b>{fmtCred(p.custo_real.credits)} · {fmtUsdBr(p.custo_real.usd)} · {fmtBrlBr(p.custo_real.brl)}</b>
                  </Typography>
                </Stack>
              </Stack>
            ) : null}
            {p.montagem.status !== 'ok' && p.montagem.status !== 'montando' ? (
              <Button
                variant="contained"
                disabled={ocupado != null || !MODULOS.every((m) => p.modulos[m.id].aprovacao === 'aprovado') || semFfmpeg}
                onClick={() => acao('montar', () => montar(p.id))}
              >
                {p.montagem.status === 'erro' ? 'Montar de novo' : 'Montar vídeo final'} · 0 crédito
              </Button>
            ) : null}
          </Box>
        </>
      ) : null}
    </Stack>
  )
}
